'use client';

import { useState, useEffect } from 'react';
import { offerAPI } from '@/services/api';
import Head from 'next/head';

export default function OffersPage() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const offersData = await offerAPI.getActiveOffers();
      setOffers(offersData);
      setError('');
    } catch (err) {
      console.error('Error fetching offers:', err);
      setError('Failed to load offers: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDiscount = (type, value) => {
    if (type === 'percentage') {
      return `${value}% OFF`;
    } else if (type === 'fixed') {
      return `৳${value} OFF`;
    } else {
      return 'FREE TICKET';
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#007f80]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-8">
      <Head>
        <title>Special Offers | Bus Ticketing System</title>
        <meta name="description" content="Check out our latest special offers and discounts on bus tickets" />
      </Head>
      
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Special Offers</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Take advantage of our limited-time offers and save on your next bus journey
        </p>
      </div>
      
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      {offers.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-4 text-xl font-medium text-gray-900">No current offers</h3>
          <p className="mt-2 text-gray-500">
            We don't have any special offers running at the moment. Check back later!
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {offers.map((offer) => (
            <div key={offer.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              {offer.imageUrl ? (
                <div className="h-48 bg-gray-200 relative">
                  <img 
                    src={offer.imageUrl} 
                    alt={offer.title} 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentNode.innerHTML = '<div class="w-full h-full bg-gray-200 flex items-center justify-center"><svg class="h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div>';
                    }}
                  />
                  <div className="absolute top-4 right-4 bg-[#007f80] text-white px-3 py-1 rounded-full text-sm font-bold">
                    {formatDiscount(offer.discountType, offer.discountValue)}
                  </div>
                </div>
              ) : (
                <div className="h-48 bg-gradient-to-r from-[#007f80] to-[#006667] flex items-center justify-center relative">
                  <div className="text-white text-center">
                    <h3 className="text-2xl font-bold">{offer.title}</h3>
                    <p className="text-lg mt-2">{formatDiscount(offer.discountType, offer.discountValue)}</p>
                  </div>
                  <div className="absolute top-4 right-4 bg-white text-[#007f80] px-3 py-1 rounded-full text-sm font-bold">
                    {formatDiscount(offer.discountType, offer.discountValue)}
                  </div>
                </div>
              )}
              
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{offer.title}</h3>
                <p className="text-gray-600 mb-4">{offer.description}</p>
                
                {offer.couponCode && (
                  <div className="mb-4">
                    <div className="inline-flex items-center px-3 py-1 bg-gray-100 rounded-full">
                      <span className="text-sm font-medium text-gray-800">Use code:</span>
                      <span className="ml-2 text-sm font-bold text-[#007f80]">{offer.couponCode}</span>
                    </div>
                  </div>
                )}
                
                <div className="flex justify-between items-center text-sm text-gray-500">
                  <span>Valid until {formatDate(offer.endDate)}</span>
                  <span>Priority: {offer.priority}</span>
                </div>
                
                <div className="mt-6">
                  <button 
                    onClick={() => {
                      // In a real implementation, this would redirect to the booking page
                      // with the coupon code pre-filled
                      alert(`To use this offer, proceed to booking and enter coupon code: ${offer.couponCode || 'N/A'}`);
                    }}
                    className="w-full bg-[#007f80] text-white py-2 px-4 rounded-md hover:bg-[#006667] focus:outline-none focus:ring-2 focus:ring-[#007f80] focus:ring-offset-2 transition-colors"
                  >
                    Use Offer
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      <div className="mt-12 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Use Our Offers</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="w-12 h-12 bg-[#007f80] rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-xl">1</span>
            </div>
            <h3 className="text-lg font-semibold mb-2">Select an Offer</h3>
            <p className="text-gray-600">Choose from our current special offers that suit your travel needs.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="w-12 h-12 bg-[#007f80] rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-xl">2</span>
            </div>
            <h3 className="text-lg font-semibold mb-2">Get the Code</h3>
            <p className="text-gray-600">Note down the coupon code provided with the offer.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="w-12 h-12 bg-[#007f80] rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-xl">3</span>
            </div>
            <h3 className="text-lg font-semibold mb-2">Apply at Checkout</h3>
            <p className="text-gray-600">Enter the coupon code during booking to get your discount.</p>
          </div>
        </div>
      </div>
    </div>
  );
}